import { Component } from '@theme/component';

/**
 * Builds a city filter from the rendered event cards and filters them by city.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} filtersContainer
 * @property {HTMLElement} list
 *
 * @extends {Component<Refs>}
 */
class EventsCalendarFilter extends Component {
  requiredRefs = ['filtersContainer', 'list'];

  connectedCallback() {
    super.connectedCallback();

    this.#buildFilters();
    this.addEventListener('click', this.#handleClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('click', this.#handleClick);
  }

  #buildFilters() {
    const { filtersContainer, list } = this.refs;
    const cards = list.querySelectorAll('[data-event-city]');

    /** @type {string[]} */
    const cities = [];
    for (const card of cards) {
      if (!(card instanceof HTMLElement)) continue;
      const city = card.dataset.eventCity;
      if (city && !cities.includes(city)) cities.push(city);
    }

    if (cities.length < 2) return;

    filtersContainer.replaceChildren();

    const allButton = this.#createFilterButton('Wszystkie', 'all', true);
    filtersContainer.append(allButton);

    for (const city of cities) {
      filtersContainer.append(this.#createFilterButton(city, city, false));
    }

    filtersContainer.hidden = false;
  }

  /**
   * @param {string} label
   * @param {string} city
   * @param {boolean} isActive
   */
  #createFilterButton(label, city, isActive) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `events-calendar__filter ${isActive ? 'button' : 'button-secondary'}`;
    button.dataset.cityFilter = '';
    button.dataset.city = city;
    button.textContent = label;
    return button;
  }

  /**
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const filterButton = target.closest('[data-city-filter]');
    if (!(filterButton instanceof HTMLElement)) return;

    this.#setActiveCity(filterButton.dataset.city ?? 'all');
  };

  /**
   * @param {string} city
   */
  #setActiveCity(city) {
    const { filtersContainer, list } = this.refs;

    for (const button of filtersContainer.querySelectorAll('[data-city-filter]')) {
      const active = button.dataset.city === city;
      button.classList.toggle('button', active);
      button.classList.toggle('button-secondary', !active);
    }

    const cards = list.querySelectorAll('[data-event-city]');

    for (const card of cards) {
      if (!(card instanceof HTMLElement)) continue;
      const matches = city === 'all' || card.dataset.eventCity === city;
      card.hidden = !matches;
    }
  }
}

if (!customElements.get('events-calendar-filter')) {
  customElements.define('events-calendar-filter', EventsCalendarFilter);
}
